/**
 * VerifyEmailView — Handles email verification link landing
 * and "check your inbox" screen with resend.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';
import { verifyEmail, resendVerification } from '../AuthService';

export async function mountVerifyEmailView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: {
    onNavigate: (view: string, params?: Record<string, string>) => void;
    token?: string;
    email?: string;
  },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_verify_email_title'));
  layout.setSubtitle('');

  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  // If token provided, verify it automatically
  if (opts.token) {
    layout.card.innerHTML = `
      <div style="text-align:center;padding:24px 0;">
        <div class="auth-spinner" style="margin:0 auto 16px;width:32px;height:32px;border-width:3px;"></div>
        <p style="color:var(--text-secondary,#94A3B8);font-size:0.9rem;">${t('auth_verifying_email')}</p>
      </div>
    `;

    const result = await verifyEmail(opts.token);

    if (result.ok) {
      layout.showSuccess(t('auth_email_verified_success'));
      layout.card.innerHTML = `
        <div style="text-align:center;padding:16px 0;">
          <div style="width:64px;height:64px;border-radius:50%;background:rgba(16,185,129,0.12);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 style="font-size:1.1rem;font-weight:700;color:var(--text-primary,#F1F5F9);margin:0 0 8px;">${t('auth_email_verified_heading')}</h2>
          <p style="font-size:0.9rem;color:var(--text-secondary,#94A3B8);margin:0 0 20px;">${t('auth_email_verified_message')}</p>
          <button class="auth-btn auth-btn-primary" id="auth-verify-login">${t('auth_go_to_login')}</button>
        </div>
      `;
      layout.card.querySelector('#auth-verify-login')?.addEventListener('click', () => opts.onNavigate('login'));
    } else {
      let msg = t(`auth_error_${result.error.code?.toLowerCase() || 'unknown'}`);
      if (msg.startsWith('auth_error_')) msg = result.error.error;
      layout.showError(msg);
      _showResendScreen(layout, t, opts);
    }
  } else {
    // "Check your inbox" mode
    _showResendScreen(layout, t, opts);
  }

  return { unmount() { container.innerHTML = ''; } };
}

function _showResendScreen(
  layout: ReturnType<typeof createAuthLayout>,
  t: (key: string) => string,
  opts: {
    onNavigate: (view: string, params?: Record<string, string>) => void;
    email?: string;
  },
): void {
  const email = opts.email || '';
  layout.card.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'auth-check-email';

  wrap.innerHTML = `
    <div class="auth-check-email-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    </div>
    <h2>${t('auth_check_email_heading')}</h2>
    <p>${t('auth_check_inbox_message')}</p>
  `;

  if (email) {
    // Resend button
    const resendBtn = document.createElement('button');
    resendBtn.className = 'auth-btn auth-btn-secondary';
    resendBtn.textContent = t('auth_resend_verification');
    resendBtn.style.marginBottom = '12px';
    resendBtn.addEventListener('click', async () => {
      resendBtn.disabled = true;
      resendBtn.textContent = t('auth_verification_sent');
      await resendVerification(email);
      setTimeout(() => {
        resendBtn.disabled = false;
        resendBtn.textContent = t('auth_resend_verification');
      }, 30000);
    });
    wrap.appendChild(resendBtn);
  }

  const loginBtn = document.createElement('button');
  loginBtn.className = 'auth-btn auth-btn-primary';
  loginBtn.textContent = t('auth_go_to_login');
  loginBtn.addEventListener('click', () => opts.onNavigate('login'));
  wrap.appendChild(loginBtn);

  layout.card.appendChild(wrap);
}
